import warningIcon from '@xero/xui-icon/icons/warning';
import infoIcon from '@xero/xui-icon/icons/info';
import XUIIcon from '@xero/xui/react/icon';
import { XUIOverviewBlock } from '@xero/xui/react/overviewblock';
import { XUIPanelSection } from '@xero/xui/react/panel';
import XUISelectBox, { XUISelectBoxOption } from '@xero/xui/react/selectbox';
import React, { FC, useEffect, useLayoutEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DropDownDatePicker } from '../../../../components/DropDownDatePicker';
import { InfoPopover } from '../../../../components/InfoPopover';
import Link from '../../../../components/Link';
import ProgressiveDisclosure from '../../../../components/ProgressiveDisclosure';
import TaxDisclaimer from '../../../../components/TaxDisclaimer';
import { middayOfTodayDate } from '../../../../helpers/date';
import {
    IShopifySettingsPutRequest,
    IShopifySettingsResponse,
    IShopifyTaxRegion,
    ITaxAuthority,
    IXeroTaxRate,
} from '../../../../models';
import { installationSelectors } from '../../../../redux/slices/InstallationSlice';
import { organisationSelectors } from '../../../../redux/slices/OrganisationSlice';
import { settingsSelectors, updateSettingsSuccess } from '../../../../redux/slices/SettingsSlice';
import { useAppDispatch } from '../../../../redux/store';
import { OrganisationClass } from '../../../../static/constants';
import './AdditionalSettingsTab.css';
import { useDropdownDatePicker } from '../../../../components/DropDownDatePicker/DropDownDatePickerHook';
import { OrganisationRegion } from '../../../../static/constants/organisationRegion';

export interface IAdditionalSettingsTabProps {
    integrationLang: Record<string, any>;
    onValid: (isValid: boolean) => void;
    onDateRangeSelect: (from: Date | null, to: Date | null) => void;
    onTaxRateSelect: (
        taxAuthorities: ITaxAuthority[],
        selectedTaxAuthority: string,
        selectedTaxType: string,
        selectedTaxRate: number
    ) => void;
    onShopifyRegionSelect: (
        taxAuthorities: ITaxAuthority[],
        selectedTaxAuthority: string,
        selectedShopifyRegion: string
    ) => void;
    setShowRefreshPlanError: React.Dispatch<React.SetStateAction<boolean>>;
    isTaxEnabledForRegion: boolean;
}

export const AdditionalSettingsTab: FC<IAdditionalSettingsTabProps> = ({
    integrationLang,
    onValid,
    onDateRangeSelect,
    onTaxRateSelect,
    onShopifyRegionSelect,
    setShowRefreshPlanError,
    isTaxEnabledForRegion,
}) => {
    const dispatch = useAppDispatch();

    const installation = useSelector(installationSelectors.installation);
    const receivedSettings = useSelector(settingsSelectors.settings) as IShopifySettingsResponse;
    const settings = useSelector(settingsSelectors.settings) as IShopifySettingsPutRequest;
    const syncFromDateUtc = settings.syncFromDateUtc ? new Date(settings.syncFromDateUtc) : null;
    const syncToDateUtc = settings.syncToDateUtc ? new Date(settings.syncToDateUtc) : null;
    const organisation = useSelector(organisationSelectors.organisation);

    const { selectedRange, buttonText, setButtonText, getDateDiff, handleDateRangeOnBlur, setSelectedRange } =
        useDropdownDatePicker({
            settings,
            integrationLang,
            onDateRangeSelect,
            maxEnabledDays: 90
        });

    const taxAuthorities = [...(settings.taxAuthorities || [])];
    const [taxAuthoritiesAlreadyExist, setTaxAuthoritiesAlreadyExist] = useState(taxAuthorities.length > 0);
    const [showSyncDate, setShowSyncDate] = useState(syncToDateUtc !== null || syncFromDateUtc !== null);
    const [isValidTaxRate, setIsValidTaxRate] = useState(
        taxAuthoritiesAlreadyExist && taxAuthorities.length > 0 && taxAuthorities[0].xeroTaxRate !== null
    );
    const [isValidShopifyTaxRegion, setIsValidShopifyTaxRegion] = useState(
        taxAuthoritiesAlreadyExist && taxAuthorities.length > 0 && !!taxAuthorities[0].shopifyTaxRegionCode
    );

    useEffect(() => {
        const isTaxValid =
            !taxAuthoritiesAlreadyExist || (taxAuthoritiesAlreadyExist && isValidTaxRate && isValidShopifyTaxRegion);
        const isSyncHistoryValid =
            (syncFromDateUtc === null && syncToDateUtc === null) ||
            getDateDiff(selectedRange().from, selectedRange().to) <= 90;
        onValid(isSyncHistoryValid && isTaxValid);
        // All dependencies used in a hook should be injected here.
        // This is to ensure that the application reacts to all state and state does not get out of sync.
        // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings, taxAuthoritiesAlreadyExist, isValidShopifyTaxRegion, isValidTaxRate]);

    useLayoutEffect(() => {
        setTaxAuthoritiesAlreadyExist(taxAuthorities.length > 0);
        // All dependencies used in a hook should be injected here.
        // This is to ensure that the application reacts to all state and state does not get out of sync.
        // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings]);

    const onTaxProgressiveDisclosureChange = (checked: boolean) => {
        setTaxAuthoritiesAlreadyExist(checked);
        setIsValidTaxRate(!checked);
        setIsValidShopifyTaxRegion(!checked);
        if (!checked) {
            dispatch(
                updateSettingsSuccess({
                    taxAuthorities: [],
                } as Partial<IShopifySettingsPutRequest>)
            );
        }
    };

    const onSyncProgressiveDisclosureChange = (checked: boolean) => {
        setShowSyncDate(checked);
        if (!checked) {
            onDateRangeSelect(null, null);
            setButtonText(integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_RANGE_DATE_PICKER_PLACEHOLDER);
        }
    };

    const xeroTaxRateSelect = (
        <span className='xui-u-flex'>
            {receivedSettings?.xeroRevenueTaxRates?.find((ta) => ta.taxType === getXeroTaxType(settings))?.name ||
                integrationLang.SETUP_PAGE.SYNC_TAB.TAX_RATE_SELECT}
        </span>
    );

    const shopifyRegionSelect = (
        <span className='xui-u-flex'>
            {receivedSettings?.shopifyTaxRegions?.find((str) => str.code === getShopifyRegion(settings))?.name ||
                integrationLang.SETUP_PAGE.SYNC_TAB.SHOPIFY_TAX_REGION_SELECT}
        </span>
    );

    function formatTaxRateItem(item: IXeroTaxRate): JSX.Element {
        return (
            <span className='account-select-option'>
                <h4 className='account-select-option--header xui-margin-top-2xsmall'>{item.name}</h4>
                <p className='xui-text-secondary account-select-option--body xui-margin-bottom-2xsmall'>
                    {item.taxComponents && item.taxComponents.map((tc, i) => `${tc.name} • `)}
                    {`${item.displayTaxRate}%`}
                </p>
            </span>
        );
    }

    function formatShopifyRegionItem(item: IShopifyTaxRegion): JSX.Element {
        return (
            <span className='account-select-option'>
                <h4 className='account-select-option--header xui-margin-top-2xsmall'>{item.name}</h4>
            </span>
        );
    }

    const handleTaxRateSelect = (xeroTaxRate: IXeroTaxRate) => {
        if (isTaxEnabledForRegion && taxAuthoritiesAlreadyExist) {
            if (xeroTaxRate) {
                onTaxRateSelect(
                    taxAuthorities,
                    receivedSettings?.xeroRegion!,
                    xeroTaxRate.taxType,
                    xeroTaxRate.displayTaxRate
                );
                setIsValidTaxRate(true);
            } else {
                setIsValidTaxRate(false);
            }
        }
    };

    const handleShopifyRegionSelect = (shopifyTaxRegion: IShopifyTaxRegion) => {
        if (isTaxEnabledForRegion && taxAuthoritiesAlreadyExist) {
            if (shopifyTaxRegion) {
                onShopifyRegionSelect(taxAuthorities, receivedSettings?.xeroRegion!, shopifyTaxRegion.code);
                setIsValidShopifyTaxRegion(true);
            } else {
                setIsValidShopifyTaxRegion(false);
            }
        }
    };

    const showTax = () =>
        installation.versionSupport?.currentVersion === 1 && (taxAuthoritiesAlreadyExist || isTaxEnabledForRegion);
    const showHistorySync = () => !installation.isOnboarded || !isHistorySyncDisabled();
    const isHistorySyncDisabled = () => organisation.region?.toUpperCase() === OrganisationRegion.AU
        ? organisation.class?.toUpperCase() === OrganisationClass.IGNITE
        : organisation.class?.toUpperCase() === OrganisationClass.STARTER;

    const taxSection = () => {
        return (
            <XUIPanelSection className='shopify-panel--section' heading={integrationLang.SETUP_PAGE.SYNC_TAB.TAX_TITLE}>
                <div className='content shopify-content xui-padding-top-3xlarge'>
                    <TaxDisclaimer blurb={integrationLang.SETUP_PAGE.SYNC_TAB.TAX_DISCLAIMER} showIcon={true} />
                </div>
                <div className='content shopify-content xui-padding-bottom-3xlarge'>
                    <ProgressiveDisclosure
                        trueLabel={integrationLang.SETUP_PAGE.SYNC_TAB.TAX_YES}
                        falseLabel={integrationLang.SETUP_PAGE.SYNC_TAB.TAX_NO}
                        initialState={taxAuthoritiesAlreadyExist}
                        label={integrationLang.SETUP_PAGE.SYNC_TAB.TAX_COLLECT}
                        groupName={'tax-group'}
                        qaHook='tax-pd'
                        onChange={onTaxProgressiveDisclosureChange}
                    >
                        <>
                            <div className='xui-text-label xui-fieldlabel-layout'>
                                {integrationLang.SETUP_PAGE.SYNC_TAB.TAX_AUTHORITY}
                            </div>
                            <div className='shopify-panel--store-url xui-padding-left-large xui-padding-top-small'>
                                {
                                    integrationLang.SETUP_PAGE.SYNC_TAB.TAX_AUTHORITIES[
                                        receivedSettings?.xeroRegion || ''
                                    ]
                                }
                            </div>
                            <XUISelectBox
                                buttonContent={xeroTaxRateSelect}
                                label={<div>{integrationLang.SETUP_PAGE.SYNC_TAB.TAX_RATE} </div>}
                                labelClassName='xui-padding-top'
                                qaHook='tax-rate-select'
                                isInvalid={!isValidTaxRate}
                            >
                                {receivedSettings?.xeroRevenueTaxRates?.map((ta, i) => (
                                    <XUISelectBoxOption
                                        key={i}
                                        id={'taxRate-' + i}
                                        isSelected={getXeroTaxType(settings) === ta.taxType}
                                        value={ta.taxType}
                                        onSelect={() => handleTaxRateSelect(ta)}
                                    >
                                        {formatTaxRateItem(ta!)}
                                    </XUISelectBoxOption>
                                ))}
                            </XUISelectBox>
                            <XUISelectBox
                                buttonContent={shopifyRegionSelect}
                                label={
                                    <div>
                                        {integrationLang.SETUP_PAGE.SYNC_TAB.SHOPIFY_TAX_REGION}{' '}
                                        <InfoPopover>
                                            {integrationLang.SETUP_PAGE.SYNC_TAB.SHOPIFY_TAX_REGION_HINT}
                                        </InfoPopover>
                                    </div>
                                }
                                labelClassName='xui-padding-top'
                                qaHook='shopify-region-select'
                                isInvalid={!isValidShopifyTaxRegion}
                            >
                                {receivedSettings?.shopifyTaxRegions?.map((region, i) => (
                                    <XUISelectBoxOption
                                        key={i}
                                        id={'shopifyRegion-' + i}
                                        isSelected={getShopifyRegion(settings) === region.code}
                                        value={region.code}
                                        onSelect={() => handleShopifyRegionSelect(region)}
                                    >
                                        {formatShopifyRegionItem(region!)}
                                    </XUISelectBoxOption>
                                ))}
                            </XUISelectBox>
                        </>
                    </ProgressiveDisclosure>
                </div>
            </XUIPanelSection>
        );
    };

    const syncHistorySection = () => {
        const isHistorySyncReadOnly = !!installation.isOnboarded || !!installation.isUpgrading;
        return (
            <XUIPanelSection
                className='shopify-panel--section'
                heading={integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_TITLE}
            >
                <div className='content shopify-content xui-padding-bottom-3xlarge'>
                    {isHistorySyncDisabled() ? (
                        <div className='xui-padding-top-2xlarge history-sync-disabled'>
                            <span className='history-sync-disabled-text'>
                                { organisation.region?.toUpperCase() === OrganisationRegion.AU
                                    ? integrationLang.SETUP_PAGE.SYNC_TAB.AU_SYNC_DISABLED_BLURB
                                    : integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_DISABLED_BLURB}
                            </span>
                            <Link
                                displayLinkIcon={true}
                                href={integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_DISABLED_LINK}
                                onClick={() => setShowRefreshPlanError(true)}
                            >
                                {integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_DISABLED_LINK_TEXT}
                            </Link>
                        </div>
                    ) : (
                        <ProgressiveDisclosure
                            trueLabel={integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_YES}
                            falseLabel={integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_NO}
                            initialState={showSyncDate}
                            label={
                                <>
                                    {integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_PROGRESSIVE_DISCLOSURE_LABEL}
                                    <span className='xui-text-deemphasis xui-margin-left-2xsmall'>
                                        {integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_OPTIONAL}
                                    </span>
                                </>
                            }
                            groupName={'date-sync-group'}
                            isDisabled={isHistorySyncReadOnly}
                            description={integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_BLURB}
                            qaHook='sync-pd'
                            onChange={onSyncProgressiveDisclosureChange}
                        >
                            <>
                                <XUIOverviewBlock
                                    hasBorder={false}
                                    hasBackground={true}
                                    textAlignment='left'
                                    className='xui-padding-large'
                                >
                                    <div>
                                        <XUIIcon icon={warningIcon} className='xui-margin-right-small' />
                                        <span className='xui-text-emphasis'>
                                            {integrationLang.SETUP_PAGE.SYNC_TAB.DISCLAIMER_TITLE}
                                        </span>
                                    </div>
                                    <ul className='xui-padding-left-large'>
                                        <li>{integrationLang.SETUP_PAGE.SYNC_TAB.DISCLAIMER_DETAILS_1}</li>
                                        <li>{integrationLang.SETUP_PAGE.SYNC_TAB.DISCLAIMER_DETAILS_2}</li>
                                        <li>{integrationLang.SETUP_PAGE.SYNC_TAB.DISCLAIMER_DETAILS_3}</li>
                                    </ul>
                                </XUIOverviewBlock>
                                <DropDownDatePicker
                                    buttonContent={buttonText}
                                    buttonLabel={
                                        <div>
                                            {integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_RANGE_DATE_PICKER}
                                            <span className='xui-text-deemphasis xui-margin-left-2xsmall'>
                                                {integrationLang.SETUP_PAGE.SYNC_TAB.SYNC_OPTIONAL}
                                            </span>
                                        </div>
                                    }
                                    onBlur={handleDateRangeOnBlur}
                                    selectedRange={selectedRange()}
                                    setSelectedRange={setSelectedRange}
                                    pickerDateEnabledTo={middayOfTodayDate()}
                                    isDisabled={isHistorySyncReadOnly}
                                    maxEnabledDays={90}
                                    showValidationError={false}
                                />
                                <div className='xui-text-align-left xui-stepper-link-description'>
                                    {integrationLang.SETUP_PAGE.SYNC_TAB.CHOOSE_SYNC_DATE_HELPER_TEXT}
                                </div>
                            </>
                        </ProgressiveDisclosure>
                    )}
                </div>
            </XUIPanelSection>
        );
    };

    const noAdditionalSettingsSection = () => {
        return (
            <XUIPanelSection
                heading={<XUIIcon icon={infoIcon} />}
                className='shopify-panel--section xui-padding-top xui-padding-bottom-3xlarge'
            >
                <div className='content shopify-content'>
                    <div className='xui-heading-medium xui-text-align-center xui-padding-bottom'>
                        {integrationLang.SETUP_PAGE.SYNC_TAB.NO_SETTINGS_REQUIRED_TITLE}
                    </div>
                    <div className='xui-text-align-center'>
                        {integrationLang.SETUP_PAGE.SYNC_TAB.NO_SETTINGS_REQUIRED_TEXT}
                    </div>
                </div>
            </XUIPanelSection>
        );
    };

    return (
        <>
            {showTax() ? taxSection() : null}
            {showHistorySync() ? syncHistorySection() : null}
            {!showTax() && !showHistorySync() ? noAdditionalSettingsSection() : null}
        </>
    );
};

const getXeroTaxType = (settings: IShopifySettingsPutRequest) =>
    settings.taxAuthorities && settings.taxAuthorities.length > 0 && settings.taxAuthorities[0].xeroTaxType;

const getShopifyRegion = (settings: IShopifySettingsPutRequest) =>
    settings.taxAuthorities && settings.taxAuthorities.length > 0 && settings.taxAuthorities[0].shopifyTaxRegionCode;
